import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Package, MapPin, Calendar, Clock, Wallet, MessageCircle } from "lucide-react";

const WHATSAPP_NUMBER = "351920248910";

export default function OrderConfirmationPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  }, [id]);

  if (!order) return <div className="py-32 text-center text-cocoa-soft">A carregar...</div>;

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Olá! Acabei de fazer a encomenda nº ${order.id} no site. Vamos combinar o pagamento? 🍰`)}`;

  return (
    <div data-testid="order-confirmation" className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
      <div className="bg-cream border border-mint/40 rounded-3xl p-10 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-mint mb-5">
          <CheckCircle2 className="w-8 h-8 text-cocoa-dark" />
        </div>
        <p className="text-xs uppercase tracking-widest text-rose-accent">Encomenda registada</p>
        <h1 className="font-heading text-5xl text-cocoa-dark mt-3">Obrigada {order.customer_name.split(" ")[0]}!</h1>
        <p className="text-cocoa-soft mt-4 leading-relaxed">
          O resumo da sua encomenda foi enviado para o WhatsApp dos Caseirinhos. Estamos a aguardar a confirmação do sinal de 50% para começar a preparar tudo com amor.
        </p>
        <a
          data-testid="open-whatsapp-button"
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-mint hover:bg-mint/80 text-cocoa-dark px-7 py-3 rounded-full font-medium mt-6 transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Abrir conversa no WhatsApp
        </a>
        <div className="mt-8 inline-block bg-cream-alt rounded-2xl px-6 py-4">
          <div className="text-xs uppercase tracking-widest text-cocoa-soft">Nº da encomenda</div>
          <div data-testid="order-id" className="font-mono text-cocoa-dark mt-1 break-all text-sm">{order.id}</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-8">
        <div className="bg-cream rounded-2xl border border-cocoa/10 p-6">
          <div className="flex items-center gap-2 mb-3"><Calendar className="w-4 h-4 text-rose-accent" /><h3 className="font-heading text-xl text-cocoa-dark">Levantamento</h3></div>
          <p className="text-cocoa-dark font-medium">{order.pickup_date}</p>
          <p className="text-cocoa-dark flex items-center gap-1.5"><Clock className="w-3 h-3 text-cocoa-soft" /> {order.pickup_time}</p>
          <div className="mt-3 flex items-start gap-2 text-sm text-cocoa-soft">
            <MapPin className="w-4 h-4 text-rose-accent shrink-0 mt-0.5" />
            <p>Rua António Duarte Caneças nº 1<br/>2700-069 Amadora</p>
          </div>
        </div>
        <div className="bg-cream rounded-2xl border border-cocoa/10 p-6">
          <div className="flex items-center gap-2 mb-3"><Wallet className="w-4 h-4 text-rose-accent" /><h3 className="font-heading text-xl text-cocoa-dark">Pagamento</h3></div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-cocoa-soft"><span>Sinal a pagar (50%)</span><span className="text-cocoa-dark">€{(order.deposit_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-cocoa-soft"><span>No levantamento</span><span data-testid="confirmation-remaining" className="text-cocoa-dark font-medium">€{(order.remaining_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-cocoa/10 pt-2 mt-2">
              <span className="text-cocoa-dark font-medium">Total</span>
              <span className="text-cocoa-dark font-medium">€{order.total.toFixed(2)}</span>
            </div>
          </div>
          <p className="text-xs text-cocoa-soft mt-4 italic">A forma de pagamento do sinal será combinada via WhatsApp.</p>
        </div>
      </div>

      <div className="bg-cream rounded-2xl border border-cocoa/10 mt-8 p-8">
        <h2 className="font-heading text-2xl text-cocoa-dark flex items-center gap-2"><Package className="w-5 h-5 text-rose-accent" /> Itens da encomenda</h2>
        <ul className="mt-5 divide-y divide-cocoa/10">
          {order.items.map((it, idx) => (
            <li key={`${it.product_id}-${it.variant_id}-${idx}`} className="py-3 flex justify-between text-sm">
              <div>
                <p className="text-cocoa-dark font-medium">{it.name}</p>
                <p className="text-xs text-cocoa-soft">{it.variant_label} × {it.quantity}</p>
                {it.selected_flavors && Object.entries(it.selected_flavors).map(([k, f]) => f.length > 0 && (
                  <p key={k} className="text-[11px] text-cocoa-soft/80 mt-0.5"><span className="capitalize">{k}:</span> {f.join(", ")}</p>
                ))}
                {it.customization && (it.customization.theme || it.customization.colors) && (
                  <div className="text-[11px] text-cocoa-soft mt-0.5 bg-rose/10 px-2 py-1 rounded inline-block">
                    {it.customization.theme && <span>Tema: <strong className="text-cocoa-dark">{it.customization.theme}</strong></span>}
                    {it.customization.colors && <span className="ml-2">Cores: <strong className="text-cocoa-dark">{it.customization.colors}</strong></span>}
                  </div>
                )}
              </div>
              <span className="text-cocoa-dark">€{(it.price * it.quantity).toFixed(2)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="text-center mt-10">
        <Link to="/catalogo" className="inline-block bg-rose hover:bg-rose-hover text-cocoa-dark px-7 py-3 rounded-full">Continuar a saborear</Link>
      </div>
    </div>
  );
}
